import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Exactly one photo: keep in step with TASK_IMAGE_MIN / TASK_IMAGE_MAX in
// staff_backend/Controller/Task/TaskController.js.
const IMAGE_MIN = 1;
const IMAGE_MAX = 1;
const MAX_FILE_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

// Starting a task needs the car's condition photos: a delivery records the car
// before it is delivered, a pick-up records the car as it is collected.
const TaskStartModal = ({ task, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const previews = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)));

    const addFiles = (event) => {
        const picked = Array.from(event.target.files || []);
        event.target.value = "";
        const file = picked[0];
        if (!file) return;
        if (!ALLOWED.includes(file.type)) { toast.error(`${file.name}: only JPG, PNG or WebP photos`); return; }
        if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${file.name}: larger than ${MAX_FILE_MB} MB`); return; }
        setFiles([file]);
    };

    const isDelivery = String(task.taskType).toLowerCase() === "delivery";
    const ready = files.length >= IMAGE_MIN;

    const onSubmit = async () => {
        if (!ready) return;
        const formData = new FormData();
        files.forEach((file) => formData.append("taskImages", file));
        setSaving(true);
        const response = await Handler({ method: "patch", url: `/staff/task/start/${task.taskId}`, data: formData, formdata: true });
        setSaving(false);
        if (response?.success) {
            toast.success(response?.data?.message || "Task started");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to start this task"));
        }
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Start {task.taskType?.toLowerCase()} {task.taskId}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body d-flex flex-column gap-3">
                        <div>
                            <label className="form-label">{isDelivery ? "Car photo before delivery" : "Car photo at the start of pick-up"} (one photo)</label>
                            <div className="d-flex flex-wrap gap-2">
                                {previews.map((p, i) => (
                                    <div key={p.url} className="position-relative">
                                        <img src={p.url} alt={`Car ${i + 1}`} style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 8 }} />
                                        <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 p-1" style={{ lineHeight: 1 }} onClick={() => setFiles(files.filter((_, idx) => idx !== i))} aria-label="Remove photo"><ImCross size={8} /></button>
                                    </div>
                                ))}
                                {files.length < IMAGE_MAX ? (
                                    <label className="d-flex flex-column align-items-center justify-content-center border rounded-3 text-muted" style={{ width: 84, height: 84, cursor: "pointer", borderStyle: "dashed" }}>
                                        <FaCloudUploadAlt size={22} />
                                        <span className="small">Add</span>
                                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={addFiles} />
                                    </label>
                                ) : ""}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Back</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={!ready || saving}>
                            {saving ? "Starting..." : "Start task"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskStartModal;
